import { useEffect, useRef, useState } from 'react';
import { masterAdminApi } from '../services/api';
import {
  loadCertificateEmailDrafts,
  type CertificateEmailDraft,
  type CertificateEmailDrafts,
} from '../lib/certificateEmailDrafts';

interface ModuleChecklistItem {  moduleId: string;
  title: string;
  stage: string;
  completed: boolean;
}

interface AssessmentExercise {
  id: string;
  title: string;
  status: string;
  detail: string;
  payload?: unknown;
}

interface ChecklistItem {
  id: string;
  label: string;
  complete: boolean;
}

interface StudentAssessmentPayload {
  studentKey: string;
  studentName: string;
  email: string;
  modulesCompleted: ModuleChecklistItem[];
  modulesProgress: {
    completedCount: number;
    totalModules: number;
    disciplineVerified: boolean;
  };
  reflectiveJournal: string;
  partnerDialogueSummary: {
    summary: string;
    themes: string[];
    frictionHandling: string;
    coreThemes: string;
    shallowAdviceRisk: boolean;
    userTurnCount: number;
    assistantTurnCount: number;
    engagementTone: string;
  };
  assessmentExercises: AssessmentExercise[];
  certificationChecklist: ChecklistItem[];
  verificationCriteria: ChecklistItem[];
  certification: {
    competent: boolean;
    issuedAt: string | null;
    certificateAuthorized?: boolean;
  };
  certificateVerification?: {
    sentAt: string | null;
    recipientEmail: string | null;
    verifiedAt?: string | null;
    assessmentOutcome?: 'competent_mentor' | 'completion_only' | null;
  };
  programAssessment?: {
    status: string | null;
    outcome: 'competent_mentor' | 'completion_only' | null;
    completedAt: string | null;
    verifiedAt: string | null;
    summary?: {
      outcomeLabel?: string;
      summary?: string;
      criteriaMet?: number;
      attemptedEvaluation?: boolean;
      shallowAdviceRisk?: boolean;
    } | null;
  };
  certificateEmails?: CertificateEmailDrafts;
}

interface StudentAssessmentHubProps {
  studentKey: string;
  onBack: () => void;
  onCertificationUpdated: () => void;
}

export default function StudentAssessmentHub({
  studentKey,
  onBack,
  onCertificationUpdated,
}: StudentAssessmentHubProps) {
  const [assessment, setAssessment] = useState<StudentAssessmentPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingCertification, setSavingCertification] = useState(false);
  const [sendingVerification, setSendingVerification] = useState<'competent_mentor' | 'completion_only' | null>(null);
  const [mentorEmailDraft, setMentorEmailDraft] = useState<CertificateEmailDraft | null>(null);
  const [completionEmailDraft, setCompletionEmailDraft] = useState<CertificateEmailDraft | null>(null);
  const [emailDraftsLoading, setEmailDraftsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const templateDraftsRef = useRef<CertificateEmailDrafts | null>(null);

  useEffect(() => {
    if (!assessment) {
      setMentorEmailDraft(null);
      setCompletionEmailDraft(null);
      templateDraftsRef.current = null;
      return undefined;
    }

    let cancelled = false;
    setEmailDraftsLoading(true);

    const resolveDrafts = async (): Promise<CertificateEmailDrafts> => {
      if (assessment.certificateEmails) {
        return assessment.certificateEmails;
      }
      return loadCertificateEmailDrafts({
        studentName: assessment.studentName,
        studentKey: assessment.studentKey,
      });
    };

    resolveDrafts()
      .then((drafts) => {
        if (cancelled) return;
        templateDraftsRef.current = drafts;
        setMentorEmailDraft({ ...drafts.mentor });
        setCompletionEmailDraft({ ...drafts.completion });
      })
      .catch(() => {
        if (cancelled) return;
        setError('Unable to load certificate email templates.');
      })
      .finally(() => {
        if (!cancelled) setEmailDraftsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [assessment]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    masterAdminApi.getStudentAssessment(studentKey)
      .then((data) => {
        if (cancelled) return;
        setAssessment(data.assessment);
      })
      .catch(() => {
        if (cancelled) return;
        setError('Unable to load student assessment.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [studentKey]);

  const handleCertificationToggle = async () => {
    if (!assessment) return;

    setSavingCertification(true);
    setError(null);

    try {
      const nextValue = !assessment.certification.competent;
      const result = await masterAdminApi.setStudentCertification(studentKey, nextValue);
      setAssessment((current) => (
        current
          ? {
            ...current,
            certification: {
              ...result.certification,
              certificateAuthorized: result.certification.competent,
            },
            assessmentExercises: current.assessmentExercises.map((exercise) => (
              exercise.id === 'module-10-certification-portfolio'
                ? { ...exercise, status: nextValue ? 'Approved' : 'Pending' }
                : exercise
            )),
          }
          : current
      ));
      onCertificationUpdated();
    } catch {
      setError('Unable to update certification status.');
    } finally {
      setSavingCertification(false);
    }
  };

  const handleCertificateVerification = async (outcome: 'competent_mentor' | 'completion_only') => {
    if (!assessment) return;

    const draft = outcome === 'competent_mentor' ? mentorEmailDraft : completionEmailDraft;
    if (!draft?.subject.trim() || !draft.body.trim()) {
      setError('Email subject and body are required before sending.');
      return;
    }

    setSendingVerification(outcome);
    setError(null);

    try {
      const result = await masterAdminApi.sendCertificateVerification(studentKey, outcome, {
        subject: draft.subject.trim(),
        body: draft.body.trim(),
      });
      setAssessment((current) => (
        current
          ? {
            ...current,
            certification: {
              ...current.certification,
              competent: result.certificateVerification.assessmentOutcome === 'competent_mentor',
              issuedAt: result.certificateVerification.verifiedAt || current.certification.issuedAt,
              certificateAuthorized: result.certificateVerification.assessmentOutcome === 'competent_mentor',
            },
            certificateVerification: {
              sentAt: result.certificateVerification.sentAt,
              recipientEmail: result.certificateVerification.recipientEmail,
              verifiedAt: result.certificateVerification.verifiedAt,
              assessmentOutcome: result.certificateVerification.assessmentOutcome,
            },
          }
          : current
      ));
      onCertificationUpdated();
    } catch (err: unknown) {
      const reason = typeof err === 'object' && err && 'error' in err
        ? String((err as { error?: string }).error || '')
        : '';
      if (reason === 'assessment_not_complete') {
        setError('Study Partner assessment has not completed yet. The student must finish all 10 modules first.');
      } else if (reason === 'missing_email') {
        setError('This student does not have a valid email address on file.');
      } else if (reason === 'certificate_template_missing') {
        setError('Certificate template not found in public/Assets/Founder/.');
      } else {
        setError('Unable to send certificate verification email.');
      }
    } finally {
      setSendingVerification(null);
    }
  };

  if (loading) {
    return (
      <div className="org-panel-card student-assessment-hub">
        <p className="student-assessment-loading">Loading The 6-Point Assessment Breakdown…</p>
      </div>
    );
  }

  if (error && !assessment) {
    return (
      <div className="org-panel-card student-assessment-hub">
        <p className="student-assessment-error">{error}</p>
        <button type="button" className="student-assessment-back-btn" onClick={onBack}>
          [ BACK TO STUDENTS LIST ]
        </button>
      </div>
    );
  }

  if (!assessment) return null;

  const module03Exercise = assessment.assessmentExercises.find((item) => item.id === 'module-03-self-assessment');
  const synthesisExercise = assessment.assessmentExercises.find((item) => item.id === 'day-28-synthesis');
  const programAssessment = assessment.programAssessment;
  const assessmentComplete = programAssessment?.status === 'complete';
  const mentorOutcome = programAssessment?.outcome === 'competent_mentor';
  const certificateIssued = Boolean(assessment.certificateVerification?.sentAt);
  const issuedOutcome = assessment.certificateVerification?.assessmentOutcome;
  const emailReady = Boolean(assessment.email && assessment.email !== '—');
  const sendButtonsDisabled = !assessmentComplete || !emailReady || emailDraftsLoading;
  const emailFieldsDisabled = emailDraftsLoading || sendingVerification !== null;
  const mentorDraft = mentorEmailDraft;
  const completionDraft = completionEmailDraft;

  return (
    <div className="org-panel-card student-assessment-hub">
      <header className="student-assessment-header">
        <div>
          <p className="student-assessment-kicker">Screen MPL-11 · Student Assessment</p>
          <h2 className="section-header student-assessment-title">The 6-Point Assessment Breakdown</h2>
          <p className="student-assessment-subtitle">
            {assessment.studentName} · {assessment.studentKey} · {assessment.email}
          </p>
        </div>
        <button type="button" className="student-assessment-back-btn" onClick={onBack}>
          [ BACK TO STUDENTS LIST ]
        </button>
      </header>

      {error ? <p className="student-assessment-error">{error}</p> : null}

      <section className="student-assessment-section">
        <h3 className="student-assessment-section-title">1. Modules Completed</h3>
        <p className="student-assessment-section-lead">
          This tracks structural discipline. You are verifying that they have methodically worked through all 10 core
          modules from Rev 2 (from the initial Learning Commitment in Module 01 straight through to The Seal of
          Mastery in Module 10). It proves they didn&apos;t skip the foundation.
        </p>
        <p className="student-assessment-progress-meta">
          {assessment.modulesProgress.completedCount} of {assessment.modulesProgress.totalModules} modules verified
          {assessment.modulesProgress.disciplineVerified ? ' · Full curriculum discipline confirmed' : ''}
        </p>
        <ul className="student-assessment-module-checklist">
          {assessment.modulesCompleted.map((module) => (
            <li
              key={module.moduleId}
              className={`student-assessment-module-item ${module.completed ? 'is-complete' : ''}`}
            >
              <span className="student-assessment-module-check" aria-hidden="true">
                {module.completed ? '✓' : '○'}
              </span>
              <span>
                Module {module.moduleId}: {module.title}
                <span className="student-assessment-module-stage">{module.stage}</span>
              </span>
            </li>
          ))}
        </ul>
      </section>

      <section className="student-assessment-section">
        <h3 className="student-assessment-section-title">2. Reflective Journal Sanctuary</h3>
        <p className="student-assessment-section-lead">
          This is where you look at their raw self-awareness. To succeed in the classroom program, they must practice deep reflection.
          In this section, you read their private thoughts to see if they are catching themselves when they fall into
          old habits, showing that they can analyze their own behavior.
        </p>
        <div className="student-assessment-journal-panel">
          {assessment.reflectiveJournal}
        </div>
      </section>

      <section className="student-assessment-section">
        <h3 className="student-assessment-section-title">3. Interactions with AI-Study Partner (Summary Mode)</h3>
        <p className="student-assessment-section-lead">
          This is an analytical summary of how they talk to the AI-Study Partner. Instead of scrolling through
          thousands of lines of text, the system gives you a high-level breakdown:
        </p>
        <ul className="student-assessment-section-bullets">
          <li>
            <strong>Friction Handling:</strong> How do they respond when the Socratic constraints challenge their assumptions?
          </li>
          <li>
            <strong>Core Themes:</strong> Are they focusing on deep inquiry, or are they reverting to giving shallow advice?
          </li>
        </ul>
        <div className="student-assessment-summary-panel">
          <p className="student-assessment-summary-block-label">Friction Handling</p>
          <p>{assessment.partnerDialogueSummary.frictionHandling}</p>
          <p className="student-assessment-summary-block-label">Core Themes</p>
          <p>{assessment.partnerDialogueSummary.coreThemes}</p>
          <div className="student-assessment-theme-tags">
            {assessment.partnerDialogueSummary.themes.map((theme) => (
              <span key={theme} className="student-assessment-theme-tag">{theme}</span>
            ))}
          </div>
          <p className="student-assessment-summary-meta">
            {assessment.partnerDialogueSummary.userTurnCount} student turns ·
            {' '}
            {assessment.partnerDialogueSummary.assistantTurnCount} partner responses ·
            {' '}
            Tone: {assessment.partnerDialogueSummary.engagementTone}
          </p>
        </div>
      </section>

      <section className="student-assessment-section">
        <h3 className="student-assessment-section-title">4. Assessment Completed &amp; Review</h3>
        <p className="student-assessment-section-lead">
          This is the hard evidence of their coursework. It gathers their specific exercises in one place—most
          importantly, their Module 03 Self-Assessment &amp; Roadmap and their final Day 28 Synthesis blocks.
          You are reviewing their actual answers to see if they understand the mechanics of Double-Loop learning.
        </p>
        <div className="student-assessment-exercise-list">
          {module03Exercise ? (
            <article className="student-assessment-exercise-card">
              <div className="student-assessment-exercise-head">
                <h4>{module03Exercise.title}</h4>
                <span className="student-assessment-exercise-status">{module03Exercise.status}</span>
              </div>
              <p>{module03Exercise.detail}</p>
              {module03Exercise.payload ? (
                <pre className="student-assessment-exercise-payload">
                  {JSON.stringify(module03Exercise.payload, null, 2)}
                </pre>
              ) : null}
            </article>
          ) : null}
          {synthesisExercise ? (
            <article className="student-assessment-exercise-card">
              <div className="student-assessment-exercise-head">
                <h4>{synthesisExercise.title}</h4>
                <span className="student-assessment-exercise-status">{synthesisExercise.status}</span>
              </div>
              <p>{synthesisExercise.detail}</p>
            </article>
          ) : null}
          {assessment.assessmentExercises
            .filter((item) => item.id !== 'module-03-self-assessment' && item.id !== 'day-28-synthesis')
            .map((exercise) => (
              <article key={exercise.id} className="student-assessment-exercise-card">
                <div className="student-assessment-exercise-head">
                  <h4>{exercise.title}</h4>
                  <span className="student-assessment-exercise-status">{exercise.status}</span>
                </div>
                <p>{exercise.detail}</p>
              </article>
            ))}
        </div>
      </section>

      <section className="student-assessment-section">
        <h3 className="student-assessment-section-title">5. Certified Mentology Classroom Checklist &amp; Verification</h3>
        <p className="student-assessment-section-lead">
          This is your final administrative gate. You review their entire portfolio against your core criteria.
          The Study Partner also runs an automatic program assessment when all modules are complete — see section 6
          for the recommended certificate outcome before you verify and issue.
        </p>
        <ul className="student-assessment-section-bullets">
          <li>Do they listen deeply?</li>
          <li>Do they ask clean Socratic questions?</li>
          <li>Have they broken the habit of &quot;fixing&quot; things right away?</li>
        </ul>
        <p className="student-assessment-section-lead">
          If they meet your standards, you toggle the Mark as Competent switch. This writes a permanent approval
          to the local SQLite database, stamps it with a timestamp, unlocks their status, and formally authorizes
          the system to issue their official Certified Mentology Classroom Certificate.
        </p>

        <p className="student-assessment-summary-block-label">Portfolio Checklist</p>
        <ul className="student-assessment-cert-checklist">
          {assessment.certificationChecklist.map((item) => (
            <li key={item.id} className={item.complete ? 'is-complete' : ''}>
              <span aria-hidden="true">{item.complete ? '✓' : '○'}</span>
              {item.label}
            </li>
          ))}
        </ul>

        <p className="student-assessment-summary-block-label">Core Verification Criteria</p>
        <ul className="student-assessment-cert-checklist">
          {assessment.verificationCriteria.map((item) => (
            <li key={item.id} className={item.complete ? 'is-complete' : ''}>
              <span aria-hidden="true">{item.complete ? '✓' : '○'}</span>
              {item.label}
            </li>
          ))}
        </ul>

        <div className="student-assessment-competency-panel">
          <div>
            <p className="student-assessment-competency-label">Mark as Competent</p>
            <p className="student-assessment-competency-help">
              Toggle to mark this student as competent. Writes permanent approval to the local SQLite database,
              stamps a timestamp, unlocks their status, and authorizes issuance of the official Certified
              Mentology Classroom Certificate.
            </p>
            {assessment.certification.issuedAt ? (
              <p className="student-assessment-competency-meta">
                Certified: {new Date(assessment.certification.issuedAt).toLocaleString('en-AU')}
                {assessment.certification.certificateAuthorized ? ' · Certificate authorized' : ''}
              </p>
            ) : null}
          </div>
          <label className="student-assessment-toggle">
            <input
              type="checkbox"
              checked={assessment.certification.competent}
              disabled={savingCertification}
              onChange={handleCertificationToggle}
            />
            <span className="student-assessment-toggle-track" />
            <span className="student-assessment-toggle-label">
              {assessment.certification.competent
                ? 'Competent — Certified Mentology Classroom Certificate Authorized'
                : 'Not Yet Competent'}
            </span>
          </label>
        </div>
      </section>

      <section className="student-assessment-section">
        <h3 className="student-assessment-section-title">6. Admin Certificate Verification</h3>
        <p className="student-assessment-section-lead">
          When the student completes all 10 modules, the Study Partner runs the full program assessment automatically
          and you receive an alert at admin@mentology.com.au. Open this assessment page, review sections 1–5, then
          choose which certificate to verify and issue.
        </p>

        {assessmentComplete ? (
          <div className="student-assessment-summary-panel">
            <p className="student-assessment-summary-block-label">Study Partner Assessment Outcome (recommendation)</p>
            <p>{programAssessment?.summary?.outcomeLabel || (mentorOutcome ? 'Competent Mentology Mentor' : 'Course Completion')}</p>
            {programAssessment?.summary?.summary ? (
              <p className="student-assessment-summary-meta">{programAssessment.summary.summary}</p>
            ) : null}
            {programAssessment?.completedAt ? (
              <p className="student-assessment-summary-meta">
                Assessed: {new Date(programAssessment.completedAt).toLocaleString('en-AU')}
              </p>
            ) : null}
          </div>
        ) : (
          <p className="student-assessment-verification-note">
            Study Partner assessment pending — activates when the student completes all 10 modules and final journal requirements.
          </p>
        )}

        <p className="student-assessment-competency-label">Verify &amp; Issue Certificate</p>
        <p className="student-assessment-competency-help">
          Review and edit each email below before sending. The matching certificate PDF is generated and attached automatically.
          Recipient: {assessment.email}
          {certificateIssued ? (
            <>
              {' '}
              · Last issued:
              {' '}
              {new Date(assessment.certificateVerification!.sentAt!).toLocaleString('en-AU')}
              {issuedOutcome === 'competent_mentor' ? ' · Mentor Certificate' : ' · Certificate of Completion'}
            </>
          ) : null}
        </p>
        {!assessmentComplete ? (
          <p className="student-assessment-verification-note">
            Waiting for Study Partner to complete the automatic program assessment.
          </p>
        ) : null}
        {!emailReady && assessmentComplete ? (
          <p className="student-assessment-verification-note">
            A valid student email address is required before certificates can be sent.
          </p>
        ) : null}
        {certificateIssued && assessment.certificateVerification?.verifiedAt ? (
          <p className="student-assessment-competency-meta">
            Verified: {new Date(assessment.certificateVerification.verifiedAt).toLocaleString('en-AU')}
          </p>
        ) : null}

        {emailDraftsLoading ? (
          <p className="student-assessment-verification-note">Loading certificate email templates…</p>
        ) : null}

        <div className="student-assessment-email-compose-grid">
          <article className="student-assessment-email-card">
            <div className="student-assessment-email-card-head">
              <h4 className="student-assessment-email-card-title">A — Mentor Certificate Email</h4>
              <button
                type="button"
                className="student-assessment-email-reset-btn"
                disabled={!templateDraftsRef.current || emailDraftsLoading}
                onClick={() => {
                  if (templateDraftsRef.current) {
                    setMentorEmailDraft({ ...templateDraftsRef.current.mentor });
                  }
                }}
              >
                Reset template
              </button>
            </div>
            <p className="student-assessment-email-attachment">
              Attachment: <strong>{mentorDraft?.attachmentFilename || 'MENTOR CERTIFICATE.pdf'}</strong> (generated on send)
            </p>
            <label className="student-assessment-email-field">
              <span className="student-assessment-email-field-label">Subject</span>
              <input
                type="text"
                className="student-assessment-email-input"
                value={mentorDraft?.subject || ''}
                disabled={emailFieldsDisabled || !mentorDraft}
                onChange={(event) => {
                  setMentorEmailDraft((current) => (
                    current ? { ...current, subject: event.target.value } : current
                  ));
                }}
              />
            </label>
            <label className="student-assessment-email-field">
              <span className="student-assessment-email-field-label">Email body</span>
              <textarea
                className="student-assessment-email-textarea"
                rows={14}
                value={mentorDraft?.body || ''}
                disabled={emailFieldsDisabled || !mentorDraft}
                onChange={(event) => {
                  setMentorEmailDraft((current) => (
                    current ? { ...current, body: event.target.value } : current
                  ));
                }}
              />
            </label>
            <button
              type="button"
              className="student-assessment-verification-btn student-assessment-verification-btn-mentor"
              disabled={sendButtonsDisabled || sendingVerification !== null || !mentorDraft}
              onClick={() => { void handleCertificateVerification('competent_mentor'); }}
            >
              {sendingVerification === 'competent_mentor'
                ? 'Sending…'
                : 'A — Verify Mentor Certificate & Send Email'}
            </button>
          </article>

          <article className="student-assessment-email-card">
            <div className="student-assessment-email-card-head">
              <h4 className="student-assessment-email-card-title">B — Certificate of Completion Email</h4>
              <button
                type="button"
                className="student-assessment-email-reset-btn"
                disabled={!templateDraftsRef.current || emailDraftsLoading}
                onClick={() => {
                  if (templateDraftsRef.current) {
                    setCompletionEmailDraft({ ...templateDraftsRef.current.completion });
                  }
                }}
              >
                Reset template
              </button>
            </div>
            <p className="student-assessment-email-attachment">
              Attachment: <strong>{completionDraft?.attachmentFilename || 'Certificate of Completion.pdf'}</strong> (generated on send)
            </p>
            <label className="student-assessment-email-field">
              <span className="student-assessment-email-field-label">Subject</span>
              <input
                type="text"
                className="student-assessment-email-input"
                value={completionDraft?.subject || ''}
                disabled={emailFieldsDisabled || !completionDraft}
                onChange={(event) => {
                  setCompletionEmailDraft((current) => (
                    current ? { ...current, subject: event.target.value } : current
                  ));
                }}
              />
            </label>
            <label className="student-assessment-email-field">
              <span className="student-assessment-email-field-label">Email body</span>
              <textarea
                className="student-assessment-email-textarea"
                rows={14}
                value={completionDraft?.body || ''}
                disabled={emailFieldsDisabled || !completionDraft}
                onChange={(event) => {
                  setCompletionEmailDraft((current) => (
                    current ? { ...current, body: event.target.value } : current
                  ));
                }}
              />
            </label>
            <button
              type="button"
              className="student-assessment-verification-btn student-assessment-verification-btn-completion"
              disabled={sendButtonsDisabled || sendingVerification !== null || !completionDraft}
              onClick={() => { void handleCertificateVerification('completion_only'); }}
            >
              {sendingVerification === 'completion_only'
                ? 'Sending…'
                : 'B — Verify Completion & Send Email'}
            </button>
          </article>
        </div>
      </section>
    </div>
  );
}
